import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminUsersApi,
  guildPoliciesApi,
  type GuildAccessPolicy,
  type ManualGrant,
} from '~/lib/api';
import { ErrorBoundary } from '~/components/ErrorBoundary';

/**
 * Admin user detail. Header + manual guild grant management for a single
 * user (relocated from the global Settings page). Admin-gated at the route.
 */
function UserDetailContent() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [pickerGuildId, setPickerGuildId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const userQuery = useQuery({
    queryKey: ['adminUser', id],
    queryFn: () => adminUsersApi.get(id),
    enabled: !!id,
  });

  const grantsQuery = useQuery({
    queryKey: ['adminUserGrants', id],
    queryFn: () => adminUsersApi.listGrants(id),
    enabled: !!id,
  });

  const policiesQuery = useQuery({
    queryKey: ['guildPolicies'],
    queryFn: guildPoliciesApi.list,
  });

  const addMutation = useMutation({
    mutationFn: (guildId: string) => adminUsersApi.addGrant(id, guildId),
    onSuccess: () => {
      setPickerGuildId('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['adminUserGrants', id] });
      queryClient.invalidateQueries({ queryKey: ['adminUser', id] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to add grant'),
  });

  const removeMutation = useMutation({
    mutationFn: (guildId: string) => adminUsersApi.removeGrant(id, guildId),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['adminUserGrants', id] });
      queryClient.invalidateQueries({ queryKey: ['adminUser', id] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to remove grant'),
  });

  const user = userQuery.data?.user;
  const policies: GuildAccessPolicy[] = policiesQuery.data?.policies ?? [];
  const grants: ManualGrant[] = grantsQuery.data?.grants ?? [];
  const grantedSet = new Set(grants.map((g) => g.guild_id));

  return (
    <div className="space-y-6">
      <div>
        <Link to="/settings/users" className="text-sm text-text-secondary hover:text-text-primary">
          ← Users
        </Link>
        {userQuery.isLoading ? (
          <h1 className="text-2xl font-bold text-text-primary mt-2">Loading…</h1>
        ) : !user ? (
          <h1 className="text-2xl font-bold text-loss mt-2">User not found</h1>
        ) : (
          <div className="mt-2">
            <h1 className="text-2xl font-bold text-text-primary">
              {user.username}
              {user.role === 'admin' && (
                <span className="ml-2 text-xs font-mono uppercase tracking-wider text-accent align-middle">
                  admin
                </span>
              )}
            </h1>
            <p className="text-xs font-mono text-text-muted mt-1">{user.id}</p>
          </div>
        )}
      </div>

      {user && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-1">Manual Guild Grants</h2>
          <p className="text-sm text-text-secondary mb-4">
            Permanently grant this user access to a guild's data, independent of their Discord
            membership. Survives re-login.
          </p>

          <div className="space-y-4">
            {/* Existing grants */}
            <div>
              <h3 className="text-sm font-semibold text-text-primary mb-2">Current grants</h3>
              {grantsQuery.isLoading ? (
                <p className="text-text-muted text-sm">Loading…</p>
              ) : grants.length === 0 ? (
                <p className="text-text-muted text-sm">No manual grants on this user.</p>
              ) : (
                <ul className="space-y-2">
                  {grants.map((g) => (
                    <li
                      key={g.guild_id}
                      className="flex items-center justify-between p-3 bg-surface-3 rounded-lg border border-border"
                    >
                      <div>
                        <div className="text-sm text-text-primary">
                          {g.guild_name ?? <span className="text-text-muted">(no matching policy)</span>}
                        </div>
                        <div className="text-xs font-mono text-text-muted">{g.guild_id}</div>
                      </div>
                      <button
                        onClick={() => removeMutation.mutate(g.guild_id)}
                        disabled={removeMutation.isPending}
                        className="btn btn-secondary text-sm"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Add grant */}
            <div>
              <h3 className="text-sm font-semibold text-text-primary mb-2">Add a grant</h3>
              <div className="flex gap-2">
                <select
                  value={pickerGuildId}
                  onChange={(e) => setPickerGuildId(e.target.value)}
                  className="input flex-1"
                >
                  <option value="">Pick a guild…</option>
                  {policies.map((p) => (
                    <option key={p.guild_id} value={p.guild_id} disabled={grantedSet.has(p.guild_id)}>
                      {p.guild_name} {grantedSet.has(p.guild_id) ? '(already granted)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => pickerGuildId && addMutation.mutate(pickerGuildId)}
                  disabled={!pickerGuildId || addMutation.isPending}
                  className="btn btn-primary"
                >
                  {addMutation.isPending ? 'Adding…' : 'Add'}
                </button>
              </div>
              {policies.length === 0 && (
                <p className="text-text-muted text-xs mt-2">
                  No guild policies configured yet — add one under Settings → Guild Access Policies.
                </p>
              )}
            </div>

            {error && <p className="text-loss text-sm">{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function UserDetailPage() {
  return (
    <ErrorBoundary>
      <UserDetailContent />
    </ErrorBoundary>
  );
}
